import * as store from '@attain-apps/fe/web/main/lib/store'
import type cytoscape from 'cytoscape'
import * as rxjs from 'rxjs'

import type { Feature } from '../../feature'
import { getEdgeDataDefinition } from '../elements'
import type { Manager } from '../manager'
import { refreshNodeClasses } from '../setup'

export const assignNodeInCytoscape = store.effects.create(
  (feature: Feature, manager: Manager) =>
    feature.actions.assign.observe().pipe(
      rxjs.map(({ data: { edge, chartId: orgChartId } }) => {
        const cy = manager.getInstance({ chartId: orgChartId })
        if (!cy) {
          return
        }
        // Removes old edges
        cy.edges()
          .filter(_edge => {
            if (_edge.target().id() === edge.childId) {
              // Removes existing edges to this child since it is being
              // reassigned to a new parent
              return true
            }
            if (_edge.source().id() === edge.childId) {
              // Removes any edges from this child that would cause a
              // cycle with the new edge
              const visited = new Set()
              const descendants = [_edge.target()]
              let descendant: cytoscape.NodeSingular | undefined
              while ((descendant = descendants.pop())) {
                if (descendant.id() === edge.parentId) {
                  return true
                }
                visited.add(descendant)
                descendants.push(
                  ...descendant
                    .outgoers()
                    .nodes()
                    .filter(node => !visited.has(node.id())),
                )
              }
            }
            // Doesn't remove anything else
            return false
          })
          .remove()
        // Adds new parent
        cy.add({ data: getEdgeDataDefinition(edge) })

        // Expand the parent node to show all its children (including the newly assigned one)
        const parentNode = cy.getElementById(edge.parentId)
        if (parentNode.length > 0) {
          const outgoers = parentNode.outgoers()
          // Make all direct children and their edges visible
          outgoers.style('display', 'element')

          // Update only the affected parent node's classes
          const children = parentNode.outgoers('node')
          const hasVisibleChild = children.some(
            child => (child.style('display') as string) !== 'none',
          )
          if (hasVisibleChild) {
            parentNode.addClass('expanded')
            parentNode.removeClass('collapsed')
          }
        }

        refreshNodeClasses(cy, {
          chartId: orgChartId,
          feature,
        })

        // Apply layout to accommodate new node
        manager.resetLayout({ chartId: orgChartId })
      }),
      rxjs.ignoreElements(),
    ),
)
